import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	inject,
	input,
	OnChanges,
	OnInit,
	output,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { cmmsEntityCategories } from '@app/pages/cmms/configs/cmms-entity-categories';
import { FormFieldLabelComponent } from '@app/shared/ui-components/form-field-label/form-field-label.component';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

@Component({
	selector: 'entity-category',
	imports: [
		MatFormFieldModule,
		MatSelectModule,
		NgxTolgeeModule,
		FormFieldLabelComponent,
		ReactiveFormsModule,
		MatIconModule,
		MatSelectModule,
		NgxMatSelectSearchModule,
		MatChipsModule,
		MatMenuModule,
	],
	templateUrl: './entity-category.component.html',
	styleUrl: './entity-category.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityCategoryComponent implements OnInit, OnChanges {
	private readonly destroyRef = inject(DestroyRef);
	private readonly translateService = inject(TranslateService);
	// Inputs for all
	selectedCategory = input<string | null>();

	// Inputs for chip display
	showAsChip = input<boolean>(false);

	// Inputs for templates
	canEditTemplate = input<boolean>(true);

	// Inputs for filters
	placeholder = input<string>('category_none');
	showRemoveButton = input<boolean>(false);

	// Outputs
	categoryChanged = output<string>();
	compareWith = (
		o1: { key: string; value: string },
		o2: { key: string; value: string }
	) => o1.key === o2.key;

	category: FormControl<{ key: string; value: string }> = new FormControl<{
		key: string;
		value: string;
	}>({ key: '', value: '' });

	categorySearch: FormControl<string> = new FormControl<string>('');
	categorySearchStringValue = toSignal(this.categorySearch.valueChanges);
	defaultCategories = computed(() => {
		const searchString = (this.categorySearchStringValue() || '').toLowerCase();
		return cmmsEntityCategories.filter(cat => {
			const translatedCat = this.translateService.instant(cat.value);
			return (
				translatedCat.toLowerCase().includes(searchString) ||
				cat.value.toLowerCase().includes(searchString)
			);
		});
	});

	ngOnInit() {
		this.category.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(value => {
				if (value.key !== this.selectedCategory()) {
					this.categoryChanged.emit(value.key);
				}
			});
	}

	ngOnChanges() {
		const category = cmmsEntityCategories.find(
			cat => cat.key === this.selectedCategory()
		);
		this.category.setValue(category || { key: null, value: 'category_none' });
		if (!this.canEditTemplate()) {
			this.category.disable();
			return;
		}
		this.category.enable();
	}
}
